import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../components/auth/AuthProvider';

export type AuthView = 'sign_in' | 'sign_up' | 'forgotten_password' | 'update_password';

export type MessageType = 'success' | 'warn';
export type Message = {
  text: string;
  type: MessageType;
} | null;

export function useAuthForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = useCallback(() => {
    setEmail('');
    setPassword('');
    setLoading(false);
  }, []);

  const handleSignIn = useCallback(async () => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Check if the error is about user not found or invalid credentials
      if (
        error.message.includes('Invalid login credentials') ||
        error.message.includes('User not found') ||
        error.message.includes('Email not confirmed')
      ) {
        // For email not confirmed, provide specific guidance
        if (error.message.includes('Email not confirmed')) {
          throw new Error(
            'Please check your email and click the confirmation link before signing in.',
          );
        }

        // For invalid credentials, provide helpful guidance
        throw new Error(
          'No account found with this email, or incorrect password. Try "Get started" to create an account.',
        );
      }

      // For other errors, throw the original error
      throw error;
    }
  }, [email, password]);

  const handleSignUp = useCallback(async () => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });

    // If signup fails because user already exists, try to sign them in
    if (error) {
      // Check if the error is about user already existing
      if (
        error.message.includes('already registered') ||
        error.message.includes('already exists') ||
        error.message.includes('User already registered')
      ) {
        // Attempt to sign in the user
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          // If sign in fails, show a helpful message
          throw new Error(
            'This email is already registered. Please check your password and try signing in instead.',
          );
        }

        // Sign in successful - user is now logged in
        return {
          text: "Welcome back! You've been signed in to your existing account.",
          type: 'success' as const,
        };
      }

      // If it's a different signup error, throw the original error
      throw error;
    }

    // Normal signup success
    return {
      text: 'Check your email to confirm your account',
      type: 'success' as const,
    };
  }, [email, password]);

  const handleForgotPassword = useCallback(async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) throw error;
    return {
      text: 'Check your email for the password reset link',
      type: 'success' as const,
    };
  }, [email]);

  const handleUpdatePassword = useCallback(async () => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });
    if (error) throw error;
    navigate('/', { replace: true });
  }, [password, navigate]);

  const handleGoogleSignIn = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        queryParams: {
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
  }, []);

  const submitForm = useCallback(
    async (view: AuthView): Promise<Message> => {
      setLoading(true);
      try {
        switch (view) {
          case 'sign_in':
            await handleSignIn();
            return null;
          case 'sign_up':
            return await handleSignUp();
          case 'forgotten_password':
            return await handleForgotPassword();
          case 'update_password':
            await handleUpdatePassword();
            return null;
          default:
            throw new Error('Invalid auth view');
        }
      } catch (err) {
        return {
          text: err instanceof Error ? err.message : 'An error occurred',
          type: 'warn',
        };
      } finally {
        setLoading(false);
      }
    },
    [handleSignIn, handleSignUp, handleForgotPassword, handleUpdatePassword],
  );

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    resetForm,
    submitForm,
    handleGoogleSignIn,
  };
}
